import { QueryClientProvider, type QueryClient } from "@tanstack/react-query";
import { lazy, Suspense, useState } from "react";
import { RouterProvider, type createBrowserRouter } from "react-router";
import { createQueryClient } from "@/api/queries";
import { useMomentQueue } from "@/moments/queue";
import { PwaUpdatePrompt } from "@/pwa/PwaUpdatePrompt";
import { Pending } from "./Pending";

type Router = ReturnType<typeof createBrowserRouter>;

// The moment host (its stage, cues, sound, the kit's Button) loads with the first moment, not with the app:
// only the queue is in the first load. Screens that play moments import `@/moments`, which carries the host,
// so it is usually already there when the first one starts.
const MomentHost = lazy(() => import("@/moments/MomentHost").then((m) => ({ default: m.MomentHost })));

function Moments() {
  const { current } = useMomentQueue();
  if (!current) return null;
  return (
    <Suspense fallback={null}>
      <MomentHost />
    </Suspense>
  );
}

const Toaster = lazy(() => import("@/components/ui/toaster").then((m) => ({ default: m.Toaster })));

// Mock mode only, and its own chunk. Tested on import.meta.env directly so real builds drop it.
const DevPanel = import.meta.env.VITE_MOCK === "1" ? lazy(() => import("@/mocks/DevPanel")) : null;

export function App({ router, queryClient }: { router: Router; queryClient?: QueryClient }) {
  const [client] = useState(() => queryClient ?? createQueryClient());
  return (
    <QueryClientProvider client={client}>
      {/* Screens suspend while their strings load (i18n) and while lazy chunks arrive. */}
      <Suspense fallback={<Pending />}>
        <RouterProvider router={router} />
      </Suspense>
      <Moments />
      <Suspense fallback={null}>
        <Toaster />
      </Suspense>
      <PwaUpdatePrompt />
      {DevPanel && (
        <Suspense fallback={null}>
          <DevPanel />
        </Suspense>
      )}
    </QueryClientProvider>
  );
}
